import React, { useMemo } from 'react';
import { Autocomplete, Box, Checkbox, TextField } from '@mui/material';
import Chip from '@mui/material/Chip';
import { theme } from '../../../theme';
import { ALL_STORES_TOKEN, MultiStorePickerProps, StoreOption } from './types';

const MultiStorePicker: React.FC<MultiStorePickerProps> = ({
  stores,
  value,
  onChange,
  allStoresLabel = '',
  label = 'Store',
  placeholder = 'Search',
  disabled = false,
  size = 'small',
  hideAllStoresOption = false,
  maxSelections,
}) => {
  const sortStoreOptionsAlphanumeric = (options: StoreOption[]): StoreOption[] => {
    const numeric: StoreOption[] = [];
    const alpha: StoreOption[] = [];
    options.forEach(o => {
      const m = o.storeName.match(/^\d+/);
      if (m) {
        o._numKey = parseInt(m[0], 10);
        numeric.push(o);
      } else {
        alpha.push(o);
      }
    });
    numeric.sort((a, b) =>
      (a._numKey ?? 0) === (b._numKey ?? 0)
        ? a.storeName.localeCompare(b.storeName)
        : (a._numKey ?? 0) - (b._numKey ?? 0),
    );
    alpha.sort((a, b) => a.storeName.localeCompare(b.storeName));
    return [...numeric, ...alpha];
  };

  const storeOptions = useMemo<StoreOption[]>(() => {
    const raw: StoreOption[] = (stores || [])
      .map(s => ({ storeName: s.storeName ?? '', storeNumber: s.storeNumber ?? '' }))
      .filter(s => s.storeName);
    const sorted = sortStoreOptionsAlphanumeric(raw);
    return hideAllStoresOption
      ? sorted
      : [
        { storeName: allStoresLabel || 'All Stores', storeNumber: ALL_STORES_TOKEN, isAll: true },
        ...sorted,
      ];
  }, [stores, allStoresLabel, hideAllStoresOption]);

  const allStoresSelected = !hideAllStoresOption && value.mode === 'all';

  const selectedStoreChips: StoreOption[] = allStoresSelected
    ? [{ storeName: allStoresLabel || 'All Stores', storeNumber: ALL_STORES_TOKEN, isAll: true }]
    : value.mode === 'specific'
      ? sortStoreOptionsAlphanumeric(value.storeNumbers.map(sn => {
        const found = stores?.find(s => s.storeNumber === sn);
        return { storeName: found?.storeName || sn, storeNumber: sn };
      }))
      : [];

  const handleStoreAutocompleteChange = (_: unknown, newValue: StoreOption[]) => {
    const selected = newValue || [];
    const pickedAll = !hideAllStoresOption && selected.some(o => o.isAll);
    const specifics = selected.filter(o => !o.isAll);

    // No selection => empty specific (so user can select stores)
    if (selected.length === 0) {
      onChange({ mode: 'specific', storeNumbers: [] });
      return;
    }

    // Only All selected
    if (pickedAll && specifics.length === 0) {
      onChange({ mode: 'all' });
      return;
    }

    // User clicked All while already in specific mode (pickedAll plus some specifics) => revert to All
    if (pickedAll && specifics.length > 0 && value.mode === 'specific') {
      onChange({ mode: 'all' });
      return;
    }

    // Mixed while previously All OR just specifics: ignore All and keep specifics
    let uniqueNumbers = Array.from(new Set(specifics.map(s => s.storeNumber)));
    if (maxSelections && uniqueNumbers.length > maxSelections) {
      uniqueNumbers = uniqueNumbers.slice(-maxSelections);
    }
    onChange({ mode: 'specific', storeNumbers: uniqueNumbers });
  };

  const baseInputHeight = size === 'small' ? 40 : 56;
  const outlinedPaddingY = size === 'small' ? 6 : 9;
  const tagMarginY = 1;
  const borderWidth = 2;
  const chipHeight = baseInputHeight - (outlinedPaddingY * 2) - (tagMarginY * 2) - borderWidth;
  const chipRowHeight = chipHeight + (tagMarginY * 2);
  const maxVisibleLines = 5;
  // Height of exactly 5 rows of chips. Only the chips scroll, in their own inner box,
  // so the outer bordered rectangle always fully encloses the scrollbar instead of
  // the border and scrollbar/content getting out of sync with each other.
  const chipsMaxHeight = chipRowHeight * maxVisibleLines;

  return (
    <Autocomplete<StoreOption, true, false, false>
      multiple
      disableCloseOnSelect
      size={size}
      disabled={disabled}
      options={storeOptions}
      getOptionLabel={(option) => option.storeName}
      isOptionEqualToValue={(option, val) => option.storeNumber === val.storeNumber}
      value={selectedStoreChips}
      onChange={handleStoreAutocompleteChange}
      sx={{
        '& .MuiOutlinedInput-root': {
          minHeight: baseInputHeight,
          flexWrap: 'wrap',
          alignItems: 'center',
        },
        '& .MuiAutocomplete-tag': {
          marginTop: `${tagMarginY}px`,
          marginBottom: `${tagMarginY}px`,
        },
        '& .MuiAutocomplete-input': {
          minWidth: '30px',
        },
      }}
      renderTags={(tagValue, getTagProps) => (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            // maxWidth (not width) lets the box shrink to fit a few chips on the same
            // line as the search input (matching the original single-line layout),
            // only spanning the full row - and therefore only wrapping/scrolling - once
            // there are enough chips to need it.
            maxWidth: '100%',
            maxHeight: `${chipsMaxHeight}px`,
            overflowY: 'auto',
          }}
        >
          {tagValue.map((option, index) => {
            const { key: _ignoreKey, ...chipProps } = getTagProps({ index });
            void _ignoreKey;
            const isAll = option.storeNumber === ALL_STORES_TOKEN;
            return (
              <Chip
                key={option.isAll ? 'ALL_STORES' : option.storeNumber || index}
                {...chipProps}
                size={size}
                label={option.storeName}
                color={isAll ? 'primary' : undefined}
                sx={{
                  height: `${chipHeight}px`,
                  fontSize: '0.875rem',
                  ...(isAll ? { backgroundColor: theme.palette.primary.main, color: '#fff' } : {}),
                }}
              />
            );
          })}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
        />
      )}
      renderOption={(props, option) => (
        <li
          {...props}
          key={option.storeNumber}
        >
          <Checkbox
            size="small"
            checked={
              option.isAll
                ? allStoresSelected
                : value.mode === 'specific' && value.storeNumbers.includes(option.storeNumber)
            }
            tabIndex={-1}
            disableRipple
            style={{ marginRight: 8 }}
          />
          {option.storeName}
        </li>
      )}
    />
  );
};

export default MultiStorePicker;
